import React, { useState, useEffect } from 'react';
import type { Court } from '../services/api';
import { getCourts, createCourt, updateCourt, deleteCourt } from '../services/api';
import eventBus from '../utils/eventBus';
import {
    Box, Typography, Button, TextField,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
    IconButton, CircularProgress, Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add'; // For 'Add New Court' button

const CourtManagement: React.FC = () => {
    const [courts, setCourts] = useState<Court[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newCourtName, setNewCourtName] = useState<string>('');
    const [editingCourtId, setEditingCourtId] = useState<number | null>(null);
    const [editingCourtName, setEditingCourtName] = useState<string>('');

    const fetchCourts = async () => {
        try {
            setLoading(true);
            const courtsData = await getCourts();
            setCourts(courtsData);
            setError(null);
            } catch (_err) {
              console.error("Error fetching courts:", _err);
              setError('Failed to fetch courts.');
            } finally {
              setLoading(false);        }
    };

    useEffect(() => {
        fetchCourts();
        
        eventBus.on('dataChanged', fetchCourts);

        return () => {
            eventBus.remove('dataChanged', fetchCourts);
        };
    }, []);

    const handleCreate = async () => {
        if (!newCourtName.trim()) {
            setError('Court name cannot be empty.');
            return;
        }
        try {
            await createCourt({ name: newCourtName });
            setIsAdding(false);
            setNewCourtName('');
            eventBus.dispatch('dataChanged');
        } catch (_err) {
            console.error("Error creating court:", _err);
            setError('Failed to create court.');
        }
    };

    const handleUpdate = async (id: number) => {
        if (!editingCourtName.trim()) {
            setError('Court name cannot be empty.');
            return;
        }
        try {
            await updateCourt(id, { name: editingCourtName });
            setEditingCourtId(null);
            setEditingCourtName('');
            eventBus.dispatch('dataChanged');
        } catch (_err) {
            console.error("Error updating court:", _err);
            setError('Failed to update court.');
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this court?')) {
            try {
                await deleteCourt(id);
                eventBus.dispatch('dataChanged');
            } catch (_err) {
                console.error("Error deleting court:", _err);
                setError('Failed to delete court.');
            }
        }
    };
    
    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
        </Box>
    );
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom>
                Manage Courts
            </Typography>

            {!isAdding && (
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => setIsAdding(true)} 
                    sx={{ mb: 2 }}
                >
                    新增場地
                </Button>
            )}
            
            {isAdding && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        label="新場地名稱"
                        variant="outlined"
                        size="small"
                        value={newCourtName}
                        onChange={(e) => setNewCourtName(e.target.value)}
                        sx={{ flexGrow: 1 }}
                    />
                    <Button variant="contained" onClick={handleCreate}>儲存</Button>
                    <Button variant="outlined" onClick={() => setIsAdding(false)}>取消</Button>
                </Box>
            )}
            
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>場地名稱</TableCell>
                            <TableCell align="right">操作</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {courts.map(court => (
                            <TableRow key={court.id}>
                                {editingCourtId === court.id ? (
                                    <>
                                        <TableCell>
                                            <TextField 
                                                value={editingCourtName} 
                                                onChange={(e) => setEditingCourtName(e.target.value)} 
                                                variant="outlined" 
                                                size="small"
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button 
                                                variant="contained" 
                                                size="small" 
                                                onClick={() => handleUpdate(court.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                儲存
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={() => setEditingCourtId(null)}
                                            >
                                                取消
                                            </Button>
                                        </TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell>{court.name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton 
                                                aria-label="edit" 
                                                onClick={() => { setEditingCourtId(court.id); setEditingCourtName(court.name); }}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton 
                                                aria-label="delete" 
                                                onClick={() => handleDelete(court.id)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default CourtManagement;
